import { NOTIFICATION_TIMEOUTS } from "common/constants";
import type { ColorLabel } from "common/constants/colorsOld";
import type { MessageType, UserNotificationType } from "common/types/userNotification";
import type { ReactNode } from "react";
import { useCallback, useEffect } from "react";
import cx from "ui/cx";
import useAppContext from "ui/hooks/useAppContext";
import IconClose from "ui/icons/IconClose";
import IconStatus from "ui/icons/status/IconStatus";
import styles from "./UserNotification.module.scss";

export function isUserNotification(notification: any): notification is UserNotificationType {
  return "message" in notification && "messageType" in notification;
}

export default function UserNotification() {
  const { userNotification, setUserNotification } = useAppContext();

  const clearNotification = useCallback(() => setUserNotification(null), []);

  useEffect(() => {
    if (!userNotification) {
      return;
    }

    const timeout = setTimeout(() => {
      clearNotification();
    }, userNotification.timeout ?? NOTIFICATION_TIMEOUTS.DEFAULT);

    return () => {
      clearTimeout(timeout);
    };
  }, [userNotification?.message]);

  if (!userNotification) {
    return null;
  }
  const { message, messageType, messageAdditional } = userNotification;

  const messageText = message instanceof Error || typeof message === "object" ? message.message : message;

  if (!messageText) {
    return null;
  }

  const classNameDesc = {
    styles,
    notification: true,
    visible: !!messageText,
    ...(messageType && { [messageType]: true }),
  };

  const classNames = cx(classNameDesc);

  const notificationIcon = getNotificationIcon(messageType ?? "default");

  return (
    <div className={styles.wrapper}>
      <div className={classNames}>
        <div className="interior">
          <div className="icon-container">{notificationIcon.type}</div>
          <div className={styles.notificationContentWrapper}>
            <div className="message-text" data-testid="user-notification-message">
              {messageText}
            </div>
            {messageAdditional && <div className={styles.messageAdditional}>{messageAdditional}</div>}
          </div>
          <div className="close-container" onClick={clearNotification}>
            <IconClose size={13.5} strokeWidth={1.65} color={notificationIcon.color} />
          </div>
        </div>
      </div>
    </div>
  );
}

function getNotificationIcon(messageType: MessageType | undefined): { type: ReactNode; color: ColorLabel } {
  const size = 20;

  function NotificationIcon() {
    return <IconStatus variant={messageType} size={size} />;
  }

  switch (messageType) {
    case "success":
      return { type: <NotificationIcon />, color: "emeraldLight" };
    case "info":
    case "default":
      return { type: <NotificationIcon />, color: "stoneLight" };
    case "warning":
      return { type: <NotificationIcon />, color: "amberDark" };
    case "error":
      return { type: <NotificationIcon />, color: "rubyLight" };
    default:
      return { type: <></>, color: "transparent" };
  }
}
